import ContactSection from "../../components/ContactForm";
import Header from "../../components/Header";
import { motion } from 'framer-motion';
import { Printer, CreditCard, FileText, ArrowRight } from 'lucide-react';
import HeroSection from "../../components/HeroSection";
import ServicesSection from "../../components/Services";
import { Link, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import { useEffect } from "react";
// import bannerImg from "../../assets/visiting-card-banner.png"; // Add your banner image path here

export default function VisitingCardPrinting() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const cardTypes = [
    {
      title: "Wooden Visiting Card",
      description: "A unique and eco-friendly option for your business card needs. Crafted from wood for a natural look."
    },
    {
      title: "Fabric Visiting Card",
      description: "Soft to the touch with a premium feel. A fabric finish offers a creative and tactile experience."
    },
    {
      title: "UV Coated Visiting Card",
      description: "A glossy finish with UV coating that enhances the design and gives a luxurious touch."
    },
    {
      title: "Spot UV Visiting Card",
      description: "With Spot UV, certain areas of the card are highlighted with a gloss finish to make your design pop."
    },
    {
      title: "350 GSM Premium Visiting Card",
      description: "High-quality, heavy stock paper for a luxurious and durable card that stands out."
    },
    {
      title: "Metal Visiting Card",
      description: "A high-end, sleek look with a metallic finish, providing a premium and professional image."
    },
    {
      title: "Magnets Visiting Card",
      description: "Combine business and practicality with a magnetic card, allowing clients to keep your information handy."
    },
    {
      title: "Linen Texture Visiting Card",
      description: "Soft yet durable with a textured linen finish, providing a high-end and unique look."
    },
    {
      title: "Transparent Visiting Cards",
      description: "A modern approach with clear plastic cards, offering a unique design and modern feel."
    },
  ];

  return (
    <div className="font-body">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark to-primary-base py-20 lg:py-32">
        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-base to-accent-light">
                Card Printing
              </span>
            </h1>
            <p className="text-xl text-neutral-200 max-w-2xl mx-auto mb-8">
              Your business card is often the first impression you make on a potential client. We offer premium visiting card printing services that help you make a lasting impression with quality and creativity.
            </p>
            <Link to="/contact" >
            <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              Get Started Today
            </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What is Visiting Card Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              What is Visiting Card Printing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Visiting cards, or business cards, are an essential marketing tool for professionals and businesses. They represent your brand and offer essential contact information at a glance. A well-designed card speaks volumes about your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Types of Visiting Cards */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Types of Visiting Cards
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cardTypes.map((card, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <h3 className="text-2xl font-bold text-primary-base mb-4">{card.title}</h3>
                <p className="text-gray-600">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Why Choose Capital Offset for Your Visiting Cards?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              At Capital Offset, we specialize in high-quality visiting card printing that leaves a lasting impression. Our professional designers ensure that your cards are designed to represent your business in the best possible light.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Printer className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Professional Design</h3>
              <p className="text-gray-600">
                Our visiting cards are designed by expert designers with a focus on clarity, creativity, and professionalism.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <CreditCard className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">High-Quality Materials</h3>
              <p className="text-gray-600">
                We use only the best materials for printing, ensuring your visiting cards feel as high-quality as they look.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <FileText className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Zero Typing Errors</h3>
              <p className="text-gray-600">
                We ensure that all the information on your card is accurate and typo-free, presenting your business in the best light.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      {/* <section className="py-20 bg-neutral-light">
        <div className="text-center">
          <div className="relative">
            <img
              src={bannerImg}
              alt="Visiting Card Examples"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section> */}

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-primary-dark to-primary-base text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white text-4xl font-bold mb-8">Get Your Visiting Cards Printed Today!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Don’t let your first impression be a missed opportunity. Reach out to us today to get your professionally designed and printed visiting cards!
          </p>
          <Link to="/contact">
          <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center mx-auto">
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
          </Link>
        </div>
      </section>

      <ServicesSection />

      {/* Contact Section */}
      <ContactSection />
      <Footer />
    </div>
  );
}
